/**
 * ChatWidget — floating AI assistant accessible from any page.
 *
 * Features:
 * - Floating button (bottom-right corner)
 * - Slide-up panel with conversation history
 * - Typing indicator while waiting for Claude
 * - Proposed action cards with confirm button
 * - Write access badge from settings
 * - Session persistence within browser session
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { executeProposedAction, sendChatMessage } from '../../api/agent';

// ── Retrieve/store session id in sessionStorage ──────────────────────────
function getStoredSessionId() {
  return sessionStorage.getItem('agent_session_id') || null;
}
function storeSessionId(id) {
  sessionStorage.setItem('agent_session_id', id);
}

export default function ChatWidget({ writeAccess = false }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getStoredSessionId);
  const [currentWriteAccess, setCurrentWriteAccess] = useState(writeAccess);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  // Focus input when opened + re-fetch write access setting
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      import('../../api/agent').then(({ getAiSettings }) => {
        getAiSettings()
          .then((s) => setCurrentWriteAccess(s.ai_write_access === 'true' || s.ai_write_access === true))
          .catch(() => {});
      });
    }
  }, [open]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    const userMsg = { id: Date.now(), role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const result = await sendChatMessage({ sessionId, message: text });

      // Store session id for continuity
      if (result.session_id && result.session_id !== sessionId) {
        setSessionId(result.session_id);
        storeSessionId(result.session_id);
      }

      const assistantMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: result.content,
        proposed_action: result.proposed_action,
        // Store the message DB id so we can execute the action
        db_message_id: result.session?.messages?.slice(-1)?.[0]?.id || null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Unknown error';
      setMessages((prev) => [
        ...prev,
        { id: Date.now() + 1, role: 'assistant', content: `Error: ${detail}`, error: true },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, sessionId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const handleConfirmAction = useCallback(async (msg) => {
    if (!msg.db_message_id) return;
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msg.id ? { ...m, executing: true } : m
      )
    );
    try {
      const result = await executeProposedAction(msg.db_message_id);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? {
                ...m,
                executing: false,
                action_done: true,
                action_result: result.message || (result.success ? 'Done.' : 'Failed.'),
                action_success: result.success,
              }
            : m
        )
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? { ...m, executing: false, action_done: true, action_result: 'Execution failed.', action_success: false }
            : m
        )
      );
    }
  }, []);

  const clearChat = () => {
    setMessages([]);
    setSessionId(null);
    sessionStorage.removeItem('agent_session_id');
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-12 h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        title="AI Assistant"
      >
        {open ? (
          <XIcon className="w-5 h-5" />
        ) : (
          <SparklesIcon className="w-5 h-5" />
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-[5rem] right-6 z-50 w-[780px] max-w-[calc(100vw-3rem)] h-[calc(100vh-7rem)] max-h-[820px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-blue-600 text-white rounded-t-xl">
            <div className="flex items-center gap-2">
              <SparklesIcon className="w-4 h-4" />
              <span className="font-semibold text-sm">AI Assistant</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                currentWriteAccess
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-500 text-blue-100 border border-blue-400'
              }`}>
                {currentWriteAccess ? 'Write On' : 'Read Only'}
              </span>
            </div>
            <button
              onClick={clearChat}
              className="text-blue-200 hover:text-white text-xs"
              title="Clear conversation"
            >
              Clear
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center pt-8">
                <SparklesIcon className="w-8 h-8 text-blue-200 mx-auto mb-2" />
                <p className="text-sm text-gray-500 font-medium">How can I help?</p>
                <div className="mt-3 space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => { setInput(s); setTimeout(() => inputRef.current?.focus(), 50); }}
                      className="block w-full text-left text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-gray-100 hover:border-blue-200 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                writeAccess={currentWriteAccess}
                onConfirmAction={handleConfirmAction}
              />
            ))}

            {loading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything about jobs, parts, vendors…"
                rows={1}
                className="flex-1 resize-none text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                style={{ maxHeight: '80px', overflowY: 'auto' }}
                disabled={loading}
              />
              <button
                onClick={send}
                disabled={!input.trim() || loading}
                className="w-8 h-8 flex-shrink-0 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 text-white rounded-lg flex items-center justify-center transition-colors"
              >
                <SendIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Markdown renderer — handles bold, tables, bullets, code, line breaks
// ---------------------------------------------------------------------------

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Table: starts with |
    if (line.trim().startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      // Filter out separator rows (---|---)
      const rows = tableLines.filter(l => !/^[\s|:-]+$/.test(l));
      if (rows.length > 0) {
        const parseRow = (row) =>
          row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
        const [headerRow, ...dataRows] = rows;
        const headers = parseRow(headerRow);
        elements.push(
          <div key={i} className="overflow-x-auto my-2">
            <table className="min-w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  {headers.map((h, j) => (
                    <th key={j} className="px-2 py-1.5 text-left font-semibold text-gray-700 border border-gray-200 whitespace-nowrap">
                      {inlineMarkdown(h.trim())}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataRows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    {parseRow(row).map((cell, ci) => (
                      <td key={ci} className="px-2 py-1.5 border border-gray-200 align-top">
                        {inlineMarkdown(cell.trim())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }
      continue;
    }

    // Bullet list
    if (/^\s*[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s/, ''));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc pl-4 my-1 space-y-0.5">
          {items.map((item, j) => (
            <li key={j} className="text-sm leading-relaxed">{inlineMarkdown(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Heading (###)
    if (/^#{1,3}\s/.test(line)) {
      elements.push(
        <p key={i} className="font-semibold text-sm mt-2 mb-0.5">
          {inlineMarkdown(line.replace(/^#{1,3}\s/, ''))}
        </p>
      );
      i++;
      continue;
    }

    // Blank line → spacer
    if (line.trim() === '') {
      elements.push(<div key={i} className="h-1.5" />);
      i++;
      continue;
    }

    // Normal paragraph
    elements.push(
      <p key={i} className="text-sm leading-relaxed">
        {inlineMarkdown(line)}
      </p>
    );
    i++;
  }

  return elements;
}

// Inline: **bold**, `code`, plain text
function inlineMarkdown(text) {
  if (!text) return null;
  const parts = [];
  const regex = /\*\*(.+?)\*\*|`([^`]+)`/g;
  let last = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={match.index} className="font-semibold">{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      parts.push(<code key={match.index} className="bg-gray-200 text-gray-800 px-1 rounded text-xs font-mono">{match[2]}</code>);
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

function MessageBubble({ msg, writeAccess, onConfirmAction }) {
  const isUser = msg.role === 'user';

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[92%] ${isUser ? '' : 'w-full'}`}>
        <div className={`rounded-xl px-3 py-2 text-sm leading-relaxed ${
          isUser
            ? 'bg-blue-600 text-white rounded-br-sm whitespace-pre-wrap'
            : msg.error
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
        }`}>
          {isUser || msg.error ? msg.content : renderMarkdown(msg.content)}
        </div>

        {/* Proposed action card */}
        {msg.proposed_action && !msg.action_done && (
          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            <p className="text-xs text-amber-700 font-medium mb-1.5">
              Proposed action:
            </p>
            <p className="text-xs text-amber-800 mb-2">
              <span className="font-mono bg-amber-100 px-1 rounded">
                {msg.proposed_action.tool}
              </span>
              {' — '}
              {JSON.stringify(msg.proposed_action.params)}
            </p>
            {writeAccess ? (
              <button
                onClick={() => onConfirmAction(msg)}
                disabled={msg.executing}
                className="w-full text-xs bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white py-1.5 rounded-md font-medium transition-colors"
              >
                {msg.executing ? 'Executing…' : (msg.proposed_action.label || 'Confirm & Execute')}
              </button>
            ) : (
              <p className="text-xs text-amber-600 italic">
                Enable Write Access in Settings to execute.
              </p>
            )}
          </div>
        )}

        {/* Action result */}
        {msg.action_done && (
          <div className={`mt-1.5 text-xs px-2 py-1 rounded ${
            msg.action_success ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
          }`}>
            {msg.action_success ? '✓ ' : '✗ '}{msg.action_result}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start">
      <div className="bg-gray-100 rounded-xl rounded-bl-sm px-3 py-2">
        <div className="flex gap-1 items-center h-4">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick suggestions
// ---------------------------------------------------------------------------

const SUGGESTIONS = [
  'Which jobs need attention right now?',
  'Any RFQs waiting over 7 days?',
  'What parts are late?',
  'Compare quotes for the most recent job',
];

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SparklesIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SendIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.269 20.876L5.999 12zm0 0h7.5" />
    </svg>
  );
}
